import React, { ReactNode } from 'react';

import {
  Box,
  Collapse,
  HStack,
  IconButton,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { AiOutlineDown, AiOutlineLeft } from 'react-icons/ai';

import {
  OutlinedFormFieldComponentProps,
  OutlinedFormObjectField,
} from '../types';

import { FieldContainer } from './FieldContainer';

interface ObjectFieldProps
  extends OutlinedFormFieldComponentProps<OutlinedFormObjectField> {
  children: ReactNode;
}

export const ObjectField = ({ children, field, styles }: ObjectFieldProps) => {
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: field.defaultIsOpen ?? !field.isCollapsable,
  });
  const showToolbar = field.label !== undefined || field.isCollapsable;
  const propertyContainer = field.styles?.propertyContainer;
  const fields = (
    <Stack
      mt={showToolbar ? 3 : 0}
      spacing={4}
      {...field.styles?.objectContainer}
    >
      {React.Children.map(children, (child, index) => (
        <Box
          // An array allows each nested field to occupy a different grid span.
          {...(Array.isArray(propertyContainer)
            ? propertyContainer[index]
            : propertyContainer)}
        >
          {child}
        </Box>
      ))}
    </Stack>
  );

  return (
    <FieldContainer
      fieldStyles={field.styles}
      isDisabled={field.isDisabled}
      label={
        showToolbar ? (
          <HStack w="full" justify="space-between">
            <Box>{field.label}</Box>
            {field.isCollapsable && (
              <IconButton
                minW={6}
                h={6}
                p={0}
                variant="ghost"
                aria-label={isOpen ? 'Hide fields' : 'Show fields'}
                icon={isOpen ? <AiOutlineLeft /> : <AiOutlineDown />}
                isDisabled={field.isDisabled}
                onClick={onToggle}
              />
            )}
          </HStack>
        ) : undefined
      }
      styles={styles}
    >
      {field.isCollapsable ? (
        <Collapse in={isOpen} animateOpacity>
          {fields}
        </Collapse>
      ) : (
        fields
      )}
    </FieldContainer>
  );
};
